import React, { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { DataItem, load, navigateTo, urlForDataItem } from 'scrivito'

import { CardConfig, CardField } from './cardConfig'
import { CardStatusBadge } from './CardStatusBadge'
import { ConfirmationModal } from '../../shared/modals/ConfirmationModal'
import { getRowMenu } from '../getRowMenu'
import { OpenEntityCanvasAction } from '../types'
import { Badge } from '@/Atomic/Atoms/Badge'
import {
  ControlledCheckbox,
  stateTransition,
} from '@/Atomic/Atoms/Inputs/ControlledCheckbox'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { MenuItem } from '@/Atomic/Molecules/MenuItem'
import DropdownMenu from '@/Atomic/Organisms/DropdownMenu'
import { MenuSheet } from '@/Atomic/Organisms/MenuSheet'
import { SchemaAttributes } from '@/Data/typesV2'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useModalHandler } from '@/hooks/useModalHandler'
import { useDataItemDelete } from '@/utils/CRUD/hooks/useDataItemDelete'
import { getEntityIconElement } from '@/utils/dataClassMap'
import { getVariantForEnumValue } from '@/utils/enumGroupVariant'
import { formatDateTime } from '@/utils/formatDateTime'
import { getDisplayValue } from '@/utils/getDisplayValue'
import { getDeleteModalSubtitle } from '@/Widgets/DXPDetails/utils/deleteModalSubtitle'
import { getDeleteModalTitle } from '@/Widgets/DXPDetails/utils/deleteModalTitle'

interface OverviewCardProps {
  dataItem: DataItem
  schema: DataItem
  config: CardConfig
  onOpenEntityCanvasCallbackFn?: OpenEntityCanvasAction
  onOpenDataItemDrawerClick?: (
    e: React.MouseEvent<Element, MouseEvent>,
    dataItem: DataItem,
    startInEdit?: boolean,
  ) => void
  selectRow?: (id: string, select?: boolean) => void
  isRowSelected?: (id: string) => boolean
  isRowSelectDisabled?: (id: string) => boolean
}

/**
 * Card representation of a single overview row, used on phone and tablet
 * viewports where the multi-column table does not fit. The layout is driven by
 * the entity's {@link CardConfig} (header leading element, optional title,
 * badges, and body fields) so every card-enabled entity reuses this component.
 * Mirrors the interaction model of {@link EntityTableRow}: tap the card to open
 * the drawer, the `⋯` menu for row actions, and the checkbox for multi-select.
 */
export const OverviewCard: React.FC<OverviewCardProps> = ({
  dataItem,
  schema,
  config,
  onOpenEntityCanvasCallbackFn,
  onOpenDataItemDrawerClick = () => {},
  selectRow,
  isRowSelected,
  isRowSelectDisabled,
}) => {
  const { t } = useTranslation()
  const { deleteDataItem } = useDataItemDelete(dataItem)
  const { isOpen, closeModal, openModal: openDeleteModal } = useModalHandler()
  const [rowMenuTrigger, setRowMenuTrigger] = useState<boolean>()
  const [isMenuSheetOpen, setIsMenuSheetOpen] = useState(false)
  const isPhone = useMediaQuery('(max-width: 766px)')

  const dataClassName = dataItem.dataClassName()!
  const deleteModalTitle = getDeleteModalTitle(dataClassName)
  const deleteModalSubtitle = getDeleteModalSubtitle(dataClassName)

  const rowId = dataItem.id()
  const selected = isRowSelected?.(rowId) ?? false
  const showCheckbox = config.leading === 'checkbox' && config.selectable

  const onOpenEntityCanvasClick = (
    e: React.MouseEvent,
    startInEdit: boolean = false,
  ) => {
    if (onOpenEntityCanvasCallbackFn) {
      onOpenEntityCanvasCallbackFn(
        e,
        dataItem,
        startInEdit,
        onOpenDataItemDrawerClick,
      )
    } else {
      onOpenDataItemDrawerClick(e, dataItem, startInEdit)
    }
  }

  const onNavigateToClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigateTo(dataItem)
  }

  const onOpenInNewTabClick = () => {
    load(() => {
      urlForDataItem(dataItem)
    }).then(() => {
      const url = urlForDataItem(dataItem) || undefined
      window.open(url, '_blank')
    })
  }

  const onRowMenuTrigger = (e: React.MouseEvent) => {
    e.preventDefault()
    if (isPhone) {
      setIsMenuSheetOpen(true)
    } else {
      setRowMenuTrigger(!rowMenuTrigger)
    }
  }

  const rowMenu = getRowMenu({
    dataItem,
    t,
    onNavigateToClick,
    onOpenInNewTabClick,
    onOpenEntityCanvasClick,
    openDeleteModal,
  })

  const badges = (config.badges ?? [])
    .map((badge) => {
      if (badge.cell) {
        return { key: badge.accessor, Cell: badge.cell, isCell: true as const }
      }

      const rawValue = dataItem.get(badge.accessor)
      const value = getDisplayValue({
        rawValue,
        key: badge.accessor,
        schema,
      })

      if (!value && !badge.virtual) {
        return null
      }

      const variant =
        badge.variant === 'enum'
          ? getVariantForEnumValue(
              (schema.get('attributes') as SchemaAttributes)?.[badge.accessor]
                ?.enumValues,
              rawValue,
            )
          : 'info'

      return {
        key: badge.accessor,
        value,
        variant,
        isStatus: badge.variant === 'enum' && badge.accessor === 'status',
        isCell: false as const,
      }
    })
    .filter((badge): badge is NonNullable<typeof badge> => badge !== null)

  return (
    <>
      <div
        className={`overview-card overview-card--${config.variant}`}
        onClick={onOpenEntityCanvasClick}
        onContextMenu={onRowMenuTrigger}
      >
        <div className="overview-card-header">
          {showCheckbox && (
            <div
              className="overview-card-checkbox"
              onClick={(e) => e.stopPropagation()}
            >
              <ControlledCheckbox
                checked={selected}
                disabled={isRowSelectDisabled?.(rowId)}
                onChange={() => selectRow?.(rowId, stateTransition(selected))}
              />
            </div>
          )}
          {config.leading === 'entityIcon' && (
            <div className="overview-card-avatar">
              {getEntityIconElement(dataItem, { avatarSize: 'md' })}
            </div>
          )}
          {config.titleCell ? (
            <div className="overview-card-title">
              <config.titleCell
                dataItem={dataItem}
                column={{ accessor: '', title: '' }}
                schema={schema}
              />
            </div>
          ) : config.titleAccessor ? (
            <div className="overview-card-title">
              <StringText
                variant="link"
                className="underline-on-hover-only"
                value={getDisplayValue({
                  rawValue: dataItem.get(config.titleAccessor),
                  key: config.titleAccessor,
                  schema,
                })}
                dataItem={dataItem}
                onClick={onNavigateToClick}
              />
            </div>
          ) : null}
          <div
            className="overview-card-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {isPhone ? (
              <Button
                icon="more-horizontal"
                variant="icon"
                size="sm"
                tooltip={t('tooltip.more')}
                buttonClassName="o-menu-button borderless"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setIsMenuSheetOpen(true)
                }}
              />
            ) : (
              <DropdownMenu
                menuItems={rowMenu}
                buttonClassName="borderless"
                trigger={rowMenuTrigger}
                usePortal
              />
            )}
          </div>
        </div>

        {badges.length > 0 && (
          <div className="overview-card-badges">
            {badges.map((badge) =>
              badge.isCell ? (
                <badge.Cell
                  key={badge.key}
                  dataItem={dataItem}
                  schema={schema}
                  column={{ accessor: badge.key, title: '' }}
                />
              ) : badge.isStatus ? (
                <CardStatusBadge
                  key={badge.key}
                  dataItem={dataItem}
                  schema={schema}
                />
              ) : (
                <Badge
                  key={badge.key}
                  value={badge.value}
                  variant={badge.variant}
                />
              ),
            )}
          </div>
        )}

        <div className="overview-card-details">
          {config.details.map((field) => (
            <CardFieldRow
              key={field.accessor}
              field={field}
              dataItem={dataItem}
              schema={schema}
            />
          ))}
        </div>
      </div>
      <ConfirmationModal
        variant="danger"
        isOpen={isOpen}
        close={closeModal}
        confirm={() => deleteDataItem()}
        title={t(deleteModalTitle)}
        subtitle={t(deleteModalSubtitle)}
      />
      <MenuSheet
        isOpen={isMenuSheetOpen}
        position="bottom-left"
        onBackdropClick={(e) => {
          e.stopPropagation()
          setIsMenuSheetOpen(false)
        }}
        onMobileCloseClick={(e) => {
          e.stopPropagation()
          setIsMenuSheetOpen(false)
        }}
      >
        <div className="overview-card-menu-sheet-list">
          {rowMenu.map((item, idx) => (
            <MenuItem
              key={`${item.label}-${idx}`}
              {...item}
              onClick={(e) => {
                item.onClick?.(e)
                setIsMenuSheetOpen(false)
              }}
            />
          ))}
        </div>
      </MenuSheet>
    </>
  )
}

interface CardFieldRowProps {
  field: CardField
  dataItem: DataItem
  schema: DataItem
}

/**
 * One line in the card body: an optional muted label prefix followed by the
 * value, resolved either from a reused table cell renderer or the default
 * value formatting. A `main` field is styled as the bold primary line.
 */
const CardFieldRow: React.FC<CardFieldRowProps> = ({
  field,
  dataItem,
  schema,
}) => {
  const { t } = useTranslation()
  const {
    accessor,
    labelKey,
    suffixKey,
    main,
    cell: Cell,
    date,
    dateTime,
    dateFormat,
    virtual,
  } = field

  const label = labelKey && (
    <span className="overview-card-meta-label">{t(labelKey)} </span>
  )
  const suffix = suffixKey && (
    <span className="overview-card-suffix"> {t(suffixKey)}</span>
  )
  const className = main ? 'overview-card-main' : 'overview-card-meta'

  // Custom cell renderers (async counts, reference links) manage their own
  // output and may resolve to empty; render them as-is between the label and
  // suffix. They contain their own links, so stop card-level click propagation.
  if (Cell) {
    // Skip cells backed by an empty attribute so they leave no blank row (an
    // absent company/creator). `virtual` cells (e.g. an async items count) have
    // no backing value and must always render.
    if (!virtual && !dataItem.get(accessor)) {
      return null
    }

    return (
      <span className={className}>
        {label}
        <Cell
          dataItem={dataItem}
          column={{ accessor, title: '' }}
          schema={schema}
        />
        {suffix}
      </span>
    )
  }

  let value: React.ReactNode
  if (date) {
    value = formatDateTime(dataItem.get(accessor), dateFormat ?? 'PP')
  } else if (dateTime) {
    value = formatDateTime(dataItem.get(accessor), dateFormat)
  } else {
    value = getDisplayValue({
      rawValue: dataItem.get(accessor),
      key: accessor,
      schema,
    })
  }

  if (!value) {
    return null
  }

  // The primary line is the entity's own name; render it as a link to the
  // item's page (matching the desktop table's main column). The anchor handles
  // navigation, so stop the card-level click that would open the drawer.
  if (main) {
    return (
      <span className={className}>
        <StringText
          variant="link"
          className="underline-on-hover-only"
          value={typeof value === 'string' ? value : undefined}
          dataItem={dataItem}
        />
      </span>
    )
  }

  return (
    <span className={className}>
      {label}
      {value}
      {suffix}
    </span>
  )
}
